import * as yup from 'yup'

import type { Tier } from '../../../../types'
import { FormFields } from './types'

export const currencyDecimalPlaces: Record<keyof Tier['variants']['monthly']['amounts'], number> = {
  usd: 2,
  cad: 2,
  eur: 2,
  gbp: 2,
  aud: 2,
  nzd: 2,
  sek: 2,
  nok: 2,
  dkk: 2,
  pln: 2,
  jpy: 0,
  myr: 2,
  sgd: 2,
  mxn: 2,
}

type Props = {
  isCreatingNewTier: boolean
  isFreeTierCreated: boolean
  isEditingPaidTier: boolean
}

export const getValidationSchema = ({
  isFreeTierCreated,
  isEditingPaidTier,
  isCreatingNewTier,
}: Props) => {
  const getPriceValidation = () => {
    if (isFreeTierCreated && isEditingPaidTier) {
      return yup
        .number()
        .max(1500000)
        .required()
        .test('price', 'You cannot change paid tier to free tier.', (value) => {
          if (value === 0) return false

          return true
        })
        .test('price', 'Must be a min. of $1', (value) => {
          if (typeof value !== 'undefined' && value < 1 && isEditingPaidTier) return false

          return true
        })
    }

    if (isFreeTierCreated && isCreatingNewTier) {
      return yup
        .number()
        .max(1500000)
        .required()
        .test('price', 'You cannot create more than one free tier', (value) => value !== 0)
        .min(1, 'Must a min. of $1')
    }

    return yup
      .number()
      .max(1500000)
      .required()
      .test('price', 'You cannot change paid tier to free tier.', (value) => {
        if (value === 0 && isEditingPaidTier) return false

        return true
      })
      .test('price', 'Must be a min. of $1', (value) => {
        if (typeof value !== 'undefined' && value < 1 && isEditingPaidTier) return false

        return true
      })
      .test('price', 'Must be either $0 or a min. of $1', (value) => {
        if (value === 0) return true

        if (typeof value !== 'undefined' && value < 1) return false

        return true
      })
  }

  const getCurrencyValidation = () => {
    if (isEditingPaidTier) {
      return yup.object().shape({
        cad: yup.number().required(),
        eur: yup.number().required(),
        gbp: yup.number().required(),
        aud: yup.number().required(),
        nzd: yup.number().required(),
        sek: yup.number().required(),
        nok: yup.number().required(),
        dkk: yup.number().required(),
        pln: yup.number().required(),
        jpy: yup.number().integer().required(),
        myr: yup.number().required(),
        sgd: yup.number().required(),
        mxn: yup.number().required(),
      })
    }

    return yup.object().when([FormFields.price], (price) => {
      if (parseFloat(price) === 0) {
        return yup.object().shape({
          cad: yup.number(),
          eur: yup.number(),
          gbp: yup.number(),
          aud: yup.number(),
          nzd: yup.number(),
          sek: yup.number(),
          nok: yup.number(),
          dkk: yup.number(),
          pln: yup.number(),
          jpy: yup.number().integer(),
          myr: yup.number(),
          sgd: yup.number(),
          mxn: yup.number(),
        })
      }

      return yup.object().shape({
        cad: yup.number().required(),
        eur: yup.number().required(),
        gbp: yup.number().required(),
        aud: yup.number().required(),
        nzd: yup.number().required(),
        sek: yup.number().required(),
        nok: yup.number().required(),
        dkk: yup.number().required(),
        pln: yup.number().required(),
        jpy: yup.number().integer().required(),
        myr: yup.number().required(),
        sgd: yup.number().required(),
        mxn: yup.number().required(),
      })
    })
  }

  return yup.object().shape({
    [FormFields.annualDiscount]: yup.number(),
    [FormFields.title]: yup.string().required().max(40),
    [FormFields.description]: yup.string().max(600),
    [FormFields.price]: getPriceValidation(),
    [FormFields.discount]: yup.boolean(),
    [FormFields.monthDiscount]: yup
      .number()
      .when([FormFields.discount, FormFields.discountType], (discount, discountType) => {
        if (!discount) return yup.number()
        if (discountType !== 'oneTime') return yup.number()

        return yup
          .number()
          .integer()
          .min(1)
          .max(100)
          .required()
          .test('monthDiscount', 'Must be either $0 or a min. of $1', function () {
            const { price, monthDiscount } = this.parent

            const currentPrice = price - price * (monthDiscount / 100)

            if (currentPrice === 0 || currentPrice >= 1) return true

            return false
          })
      }),
    [FormFields.yearDiscount]: yup
      .number()
      .when([FormFields.discount, FormFields.discountType], (discount, discountType) => {
        if (!discount) return yup.number().nullable(true)
        if (discountType !== 'oneTime') return yup.number().nullable(true)

        return yup
          .number()
          .integer()
          .min(1)
          .max(100)
          .nullable(true)
          .test('yearDiscount', 'Must be either $0 or a min. of $1', function () {
            const { price, yearDiscount, annualDiscount } = this.parent

            const yearPrice = price * 12 - price * 12 * (annualDiscount / 100)
            const currentPrice = yearPrice - yearPrice * (yearDiscount / 100)

            // NOTE: If there is no value for year discount (it is not required)
            if (!yearDiscount) return true
            // NOTE: If global annual discounts are disabled
            if (!annualDiscount) return true

            if (currentPrice === 0 || currentPrice >= 1) return true

            return false
          })
      }),
    [FormFields.timeLimit]: yup.boolean(),
    [FormFields.startDate]: yup.date().when(
      [FormFields.timeLimit, FormFields.discount, FormFields.discountType],
      // @ts-ignore
      (timeLimit, discount, discountType) => {
        if (discountType !== 'oneTime') return yup.string().nullable(true)

        return timeLimit && discount
          ? yup.date().required().nullable(true)
          : yup.date().nullable(true)
      },
    ),
    [FormFields.endDate]: yup.date().when(
      [FormFields.timeLimit, FormFields.discount, FormFields.discountType],
      // @ts-ignore
      (timeLimit, discount, discountType) => {
        if (discountType !== 'oneTime') return yup.string().nullable(true)

        return timeLimit && discount
          ? yup.date().required().nullable(true)
          : yup.date().nullable(true)
      },
    ),
    [FormFields.image]: yup.string(),
    [FormFields.removeImage]: yup.boolean().required(),
    [FormFields.convertInternationalPricing]: yup.boolean(),
    [FormFields.currencies]: getCurrencyValidation(),
    [FormFields.askForShippingAddress]: yup.boolean(),
  })
}
